import { FieldArray, Form, Formik } from "formik";
import { MinusCircle, PlusCircle, FileText, Users, Clock, MapPin, User, AlertTriangle, CheckCircle2, Calendar } from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { InputText } from "../../components/InputText";
import { SelectSingle } from "../../components/SelectSingle";
import { TextArea } from "../../components/TextArea";
import { FormButton } from "../../components/FormButton";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import { habboService } from "../../services/habbo";

const schema = Yup.object().shape({
  oficial: Yup.string().required("Oficial obrigatório"),
  treinador: Yup.string().required("Treinador obrigatório"),
  auxiliar: Yup.string().required("Auxiliar obrigatório"),
  patente_treinador: Yup.string().required("Patente obrigatório"),
  patente_auxiliar: Yup.string().required("Patente obrigatório"),
  tipo_relatorio: Yup.string().required("Tipo Relatório obrigatório"),
  id_treinamento: Yup.string().required("Treinamento obrigatório"),
  sala: Yup.string().required("Sala obrigatório"),
  data_treinamento: Yup.string().required("Data do treinamento obrigatória"),
  inicio_treinamento: Yup.string().required(
    "Início do treinamento obrigatório",
  ),
  termino_treinamento: Yup.string().required(
    "Término do treinamento obrigatório",
  ),
  treinados: Yup.array().of(
    Yup.object().shape({
      nick: Yup.string().required("Nick obrigatório"),
      aprovado: Yup.boolean().required("Aprovado obrigatório"),
      motivo: Yup.string().when("aprovado", {
        is: false,
        then: (schema) => schema.required("Motivo obrigatório para reprovados"),
        otherwise: (schema) => schema.notRequired(),
      }),
      horario: Yup.string().when("aprovado", {
        is: false,
        then: (schema) => schema.required("Horário obrigatório para reprovados"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  ),
});

interface FormRelatorioTreinamento {
  id_militar: string;
  oficial: string;
  treinador: string;
  auxiliar: string;
  patente_treinador: string;
  patente_auxiliar: string;
  tipo_relatorio: string;
  id_treinamento: string;
  sala: string;
  data_treinamento: string;
  inicio_treinamento: string;
  termino_treinamento: string;
  observacao: string;
  treinados: Treinados[];
  status: string;
}

interface Treinados {
  nick: string;
  motivo: string;
  horario: string;
  aprovado: boolean;
}

export function RelatorioTreinamento() {
  const { militar } = useAuth();

  const [selectOficiais, setSelectOficiais] = useState([]);
  const [selectPatentes, setSelectPatentes] = useState([]);
  const [selectTreinamentos, setSelectTreinamentos] = useState([]);
  const [nickValidationStates, setNickValidationStates] = useState<Record<number, { validating: boolean; valid: boolean | null }>>({});
  const [nickValidationTimeouts, setNickValidationTimeouts] = useState<Record<number, NodeJS.Timeout>>({});

  /**
   * Retorna a data do "dia de jogo":
   * - Se ainda não são 4h da manhã, retorna o dia anterior
   * - Caso contrário, retorna o dia atual
   * Isso garante que relatórios feitos entre 00h e 04h sejam associados ao dia anterior
   */
  const getGameDayDate = () => {
    const now = new Date();
    const brazilDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
    const brazilHour = brazilDate.getHours();

    // Se for antes das 4h, usar o dia anterior
    if (brazilHour < 4) {
      brazilDate.setDate(brazilDate.getDate() - 1);
    }

    return brazilDate.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  /**
   * Retorna a data do "dia de jogo" no formato ISO (YYYY-MM-DD)
   */
  const getGameDayDateISO = () => {
    const now = new Date();
    const brazilDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
    const brazilHour = brazilDate.getHours();

    // Se for antes das 4h, usar o dia anterior
    if (brazilHour < 4) {
      brazilDate.setDate(brazilDate.getDate() - 1);
    }

    return moment(brazilDate).format('YYYY-MM-DD');
  };

  // Helper function para obter erros de campos aninhados
  const getFieldError = (errors: any, fieldPath: string) => {
    const parts = fieldPath.split('.');
    let current = errors;

    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return undefined;
      }
    }

    return typeof current === 'string' ? current : undefined;
  };

  useEffect(() => {
    api.get("/militares/listOficiais").then(response => {
      setSelectOficiais(response.data);
    });
    api.get("/patentes").then(response => {
      setSelectPatentes(response.data);
    });
    api.get("/treinamentos").then(response => {
      setSelectTreinamentos(response.data);
    });
  }, []);

  const validateNickInHabbo = async (nick: string, index: number) => {
    setNickValidationStates(prev => ({
      ...prev,
      [index]: { validating: true, valid: null }
    }));

    try {
      const response = await habboService.checkUserExists(nick);
      setNickValidationStates(prev => ({
        ...prev,
        [index]: { validating: false, valid: response.exists }
      }));
      return response.exists;
    } catch (error) {
      console.error('Erro ao validar nick no Habbo:', error);
      setNickValidationStates(prev => ({
        ...prev,
        [index]: { validating: false, valid: null }
      }));
      return null;
    }
  };

  const handleNickChange = (index: number, value: string, setFieldValue: any) => {
    setFieldValue(`treinados.${index}.nick`, value);

    // Limpar timeout anterior
    if (nickValidationTimeouts[index]) {
      clearTimeout(nickValidationTimeouts[index]);
    }

    if (value.trim()) {
      // Marcar como validando
      setNickValidationStates(prev => ({
        ...prev,
        [index]: { validating: true, valid: null }
      }));

      // Criar novo timeout
      const timeout = setTimeout(() => {
        validateNickInHabbo(value.trim(), index);
      }, 800);

      setNickValidationTimeouts(prev => ({
        ...prev,
        [index]: timeout
      }));
    } else {
      setNickValidationStates(prev => ({
        ...prev,
        [index]: { validating: false, valid: null }
      }));
    }
  };

  function handleSubmit(values, actions) {
    // Verificar se todos os nicks foram validados
    const invalidNicks = values.treinados.some((treinado, index) => {
      const state = nickValidationStates[index];
      return treinado.nick && (!state || state.valid !== true);
    });

    if (invalidNicks) {
      enqueueSnackbar("Por favor, aguarde a validação de todos os nicks no Habbo ou corrija os nicks inválidos", {
        variant: "warning",
      });
      return;
    }

    api
      .post("/relatorioTreinamento", values)
      .then(() => {
        enqueueSnackbar("Relatório de treinamento enviado!", {
          variant: "success",
        });
        actions.resetForm();
        setNickValidationStates({});
        setNickValidationTimeouts({});
      })
      .catch(({ response }) => {
        enqueueSnackbar(response.data.message, {
          variant: "warning",
        });
      });
  }

  const initialValues: FormRelatorioTreinamento = {
    id_militar: militar?.id || "",
    oficial: "",
    treinador: "",
    auxiliar: "",
    patente_treinador: "",
    patente_auxiliar: "",
    tipo_relatorio: "treinamento",
    id_treinamento: "",
    sala: "",
    data_treinamento: getGameDayDateISO(),
    inicio_treinamento: "",
    termino_treinamento: "",
    observacao: "",
    status: "P",
    treinados: [
      {
        nick: "",
        motivo: "",
        horario: "",
        aprovado: false,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[oklch(0.45_0.15_142)] rounded-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Relatório de Treinamento</h1>
              <p className="text-gray-600 dark:text-gray-400">Registre os dados do treinamento e participantes</p>
            </div>
          </div>

      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values, actions) => {
          handleSubmit(values, actions);
        }}
      >
        {({ values, setFieldValue, errors }) => (
          <Form>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Formulário Principal */}
              <div className="lg:col-span-2">
                <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-[oklch(0.35_0.15_142)] flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Dados do Treinamento
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <SelectSingle
                          title="Tipo do Relatório"
                          name="tipo_relatorio"
                          value={values.tipo_relatorio}
                          onChange={(e) => setFieldValue('tipo_relatorio', e.target.value)}
                          error={errors.tipo_relatorio}
                        >
                          <option value="treinamento">Treinamento</option>
                          <option value="teste">Teste</option>
                        </SelectSingle>
                      </div>
                      <div>
                        <InputText
                          name="data"
                          title="Data"
                          placeholder="Data"
                          value={getGameDayDate()}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <SelectSingle
                        title="Oficial Responsável"
                        name="oficial"
                        value={values.oficial}
                        onChange={(e) => setFieldValue('oficial', e.target.value)}
                        error={errors.oficial}
                      >
                        <option value="" disabled>
                          Selecione um Oficial
                        </option>
                        {selectOficiais.map(({ nick }) => (
                          <option key={nick} value={nick}>{nick}</option>
                        ))}
                      </SelectSingle>
                    </div>
                    <div>
                      <SelectSingle
                        title="Patente Treinador"
                        name="patente_treinador"
                        value={values.patente_treinador}
                        onChange={(e) => setFieldValue('patente_treinador', e.target.value)}
                        error={errors.patente_treinador}
                      >
                        <option value="" disabled>
                          Selecione uma patente
                        </option>
                        {selectPatentes.map(({ patente }) => (
                          <option key={patente} value={patente}>{patente}</option>
                        ))}
                      </SelectSingle>
                    </div>
                    <div>
                      <InputText
                        name="treinador"
                        title="Nick Treinador"
                        placeholder="Nick"
                        value={values.treinador}
                        onChange={(e) => setFieldValue('treinador', e.target.value)}
                        error={errors.treinador}
                      />
                    </div>
                    <div>
                      <SelectSingle
                        title="Patente Auxiliar"
                        name="patente_auxiliar"
                        value={values.patente_auxiliar}
                        onChange={(e) => setFieldValue('patente_auxiliar', e.target.value)}
                        error={errors.patente_auxiliar}
                      >
                        <option value="" disabled>
                          Selecione uma patente
                        </option>
                        {selectPatentes.map(({ patente }) => (
                          <option key={patente} value={patente}>{patente}</option>
                        ))}
                      </SelectSingle>
                    </div>
                    <div>
                      <InputText
                        name="auxiliar"
                        title="Nick Auxiliar"
                        placeholder="Nick"
                        value={values.auxiliar}
                        onChange={(e) => setFieldValue('auxiliar', e.target.value)}
                        error={errors.auxiliar}
                      />
                    </div>
                    <div>
                      <SelectSingle
                        title="Treinamento"
                        name="id_treinamento"
                        value={values.id_treinamento}
                        onChange={(e) => setFieldValue('id_treinamento', e.target.value)}
                        error={errors.id_treinamento}
                      >
                        <option value="" disabled>
                          Selecione um treinamento
                        </option>
                        {selectTreinamentos.map(({ id, treinamento }) => (
                          <option key={id} value={id}>{treinamento}</option>
                        ))}
                      </SelectSingle>
                    </div>
                    <div>
                      <SelectSingle
                        title="Sala"
                        name="sala"
                        value={values.sala}
                        onChange={(e) => setFieldValue('sala', e.target.value)}
                        error={errors.sala}
                      >
                        <option value="" disabled>
                          Selecione uma sala
                        </option>
                        <option value="1">Sala 1</option>
                        <option value="2">Sala 2</option>
                        <option value="3">Sala 3</option>
                        <option value="4">Sala 4</option>
                        <option value="5">Sala 5</option>
                      </SelectSingle>
                    </div>
                    <div className="md:col-span-2 grid grid-cols-2 gap-4">
                      <InputText
                        name="inicio_treinamento"
                        title="Início Treinamento"
                        type="time"
                        value={values.inicio_treinamento}
                        onChange={(e) => setFieldValue('inicio_treinamento', e.target.value)}
                        error={errors.inicio_treinamento}
                      />
                      <InputText
                        name="termino_treinamento"
                        title="Término Treinamento"
                        type="time"
                        value={values.termino_treinamento}
                        onChange={(e) => setFieldValue('termino_treinamento', e.target.value)}
                        error={errors.termino_treinamento}
                      />
                    </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)] mt-6">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[oklch(0.35_0.15_142)] flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Lista de Treinados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FieldArray
                    name="treinados"
                    render={arrayTreinados => (
                      <div className="space-y-4">
                        {values.treinados.map((_, index) => (
                          <div key={index} className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <InputText
                                  title="Nick do Treinado"
                                  name={`treinados.${index}.nick`}
                                  placeholder="Digite o nick"
                                  value={values.treinados[index].nick}
                                  onChange={(e) => handleNickChange(index, e.target.value, setFieldValue)}
                                  error={getFieldError(errors, `treinados.${index}.nick`)}
                                  required
                                />
                                {nickValidationStates[index]?.validating && (
                                  <p className="text-sm text-blue-600 mt-1">Verificando usuário no Habbo...</p>
                                )}
                                {!nickValidationStates[index]?.validating && nickValidationStates[index]?.valid === true && (
                                  <p className="text-sm text-green-600 mt-1">✓ Usuário encontrado no Habbo</p>
                                )}
                                {!nickValidationStates[index]?.validating && nickValidationStates[index]?.valid === false && (
                                  <p className="text-sm text-red-600 mt-1">✗ Usuário não encontrado no Habbo</p>
                                )}
                              </div>
                              <div className="flex items-center gap-4">
                                <Label className="dark:text-gray-200">Status:</Label>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={values.treinados[index].aprovado}
                                    onChange={(e) => {
                                      const newValue = e.target.checked;
                                      setFieldValue(`treinados.${index}.aprovado`, newValue);

                                      // Limpar campos de reprovação quando aprovado
                                      if (newValue) {
                                        setFieldValue(`treinados.${index}.motivo`, "");
                                        setFieldValue(`treinados.${index}.horario`, "");
                                      }
                                    }}
                                  />
                                  <div className="w-11 h-6 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {values.treinados[index].aprovado ? "Aprovado" : "Reprovado"}
                                  </span>
                                </label>
                              </div>
                            </div>

                            {!values.treinados[index].aprovado && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputText
                                  type="time"
                                  name={`treinados.${index}.horario`}
                                  title="Horário da Saída"
                                  value={values.treinados[index].horario}
                                  onChange={(e) => setFieldValue(`treinados.${index}.horario`, e.target.value)}
                                  error={getFieldError(errors, `treinados.${index}.horario`)}
                                  required
                                />
                                <InputText
                                  name={`treinados.${index}.motivo`}
                                  title="Motivo da Reprovação"
                                  placeholder="Digite o motivo"
                                  value={values.treinados[index].motivo}
                                  onChange={(e) => setFieldValue(`treinados.${index}.motivo`, e.target.value)}
                                  error={getFieldError(errors, `treinados.${index}.motivo`)}
                                  required
                                />
                              </div>
                            )}

                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  arrayTreinados.push({
                                    nick: "",
                                    motivo: "",
                                    horario: "",
                                    aprovado: false,
                                  });
                                }}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <PlusCircle className="h-4 w-4 mr-1" />
                                Adicionar
                              </button>
                              {values.treinados.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => arrayTreinados.remove(index)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  <MinusCircle className="h-4 w-4 mr-1" />
                                  Remover
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />

                  <div className="mt-6">
                    <TextArea
                      name="observacao"
                      title="Observações Gerais"
                      value={values.observacao}
                      onChange={(e) => setFieldValue('observacao', e.target.value)}
                      error={errors.observacao}
                    />
                  </div>

                  <div className="flex justify-end mt-6">
                    <FormButton
                      type="submit"
                      className="min-w-48"
                    >
                      Finalizar Relatório
                    </FormButton>
                  </div>
                  </CardContent>
                </Card>
              </div>

              {/* Painel de Informações */}
              <div className="space-y-4">
                {/* Resumo do Treinamento */}
                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-blue-700 dark:text-blue-400">
                      <FileText className="h-4 w-4" />
                      Resumo do Relatório
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Data:</span>
                        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {moment().format("DD/MM/YYYY")}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Tipo:</span>
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          {values.tipo_relatorio || "Não selecionado"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Total de Treinados:</span>
                        <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                          {values.treinados.length}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Aprovados:</span>
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          {values.treinados.filter(t => t.aprovado).length}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Reprovados:</span>
                        <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          {values.treinados.filter(t => !t.aprovado).length}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Avisos Importantes */}
                <Card className="border-l-4 border-l-amber-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4" />
                      Avisos Importantes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>Verifique se os horários de início e término estão corretos.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Users className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>Certifique-se de que todos os nicks dos treinados estão corretos.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>Confirme a sala onde o treinamento foi realizado.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>Para reprovados, o motivo e horário são obrigatórios.</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </Form>
        )}
      </Formik>
        </div>
      </div>
    </div>
  );
}
